import { supabase } from '../lib/supabase';

export interface UserFollower {
  id: string;
  follower_id: string;
  following_id: string;
  created_at: string;
}

export interface PerformanceLike {
  id: string;
  user_id: string;
  performance_id: string;
  created_at: string;
}

export interface PerformanceComment {
  id: string;
  user_id: string;
  performance_id: string;
  comment: string;
  created_at: string;
  updated_at: string | null;
  // Joined fields
  username?: string;
  avatar_url?: string;
}

export interface DirectMessage {
  id: string;
  sender_id: string;
  recipient_id: string;
  message: string;
  read: boolean;
  created_at: string;
  // Joined fields
  sender_username?: string;
  sender_avatar_url?: string;
}

export class SocialService {
  async followUser(followerId: string, followingId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_followers')
        .insert([{
          follower_id: followerId,
          following_id: followingId
        }]);

      if (error) throw error;
    } catch (error) {
      console.error('Error following user:', error);
      throw error;
    }
  }

  async unfollowUser(followerId: string, followingId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_followers')
        .delete()
        .eq('follower_id', followerId)
        .eq('following_id', followingId);

      if (error) throw error;
    } catch (error) {
      console.error('Error unfollowing user:', error);
      throw error;
    }
  }

  async getFollowers(userId: string): Promise<UserFollower[]> {
    try {
      const { data, error } = await supabase
        .from('user_followers')
        .select('*')
        .eq('following_id', userId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting followers:', error);
      throw error;
    }
  }

  async getFollowing(userId: string): Promise<UserFollower[]> {
    try {
      const { data, error } = await supabase
        .from('user_followers')
        .select('*')
        .eq('follower_id', userId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting following users:', error);
      throw error;
    }
  }

  async isFollowing(followerId: string, followingId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_followers')
        .select('id')
        .eq('follower_id', followerId)
        .eq('following_id', followingId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking follow status:', error);
      throw error;
    }
  }

  async likePerformance(userId: string, performanceId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('performance_likes')
        .insert([{
          user_id: userId,
          performance_id: performanceId
        }]);

      if (error) throw error;
    } catch (error) {
      console.error('Error liking performance:', error);
      throw error;
    }
  }

  async unlikePerformance(userId: string, performanceId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('performance_likes')
        .delete()
        .eq('user_id', userId)
        .eq('performance_id', performanceId);

      if (error) throw error;
    } catch (error) {
      console.error('Error unliking performance:', error);
      throw error;
    }
  }

  async isLiked(userId: string, performanceId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('performance_likes')
        .select('id')
        .eq('user_id', userId)
        .eq('performance_id', performanceId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking like status:', error);
      throw error;
    }
  }

  async getLikes(performanceId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('performance_likes')
        .select('id', { count: 'exact', head: true })
        .eq('performance_id', performanceId);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error getting like count:', error);
      throw error;
    }
  }

  async addComment(userId: string, performanceId: string, comment: string): Promise<PerformanceComment> {
    try {
      const { data, error } = await supabase
        .from('performance_comments')
        .insert([{
          user_id: userId,
          performance_id: performanceId,
          comment
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  async getComments(performanceId: string): Promise<PerformanceComment[]> {
    try {
      const { data, error } = await supabase
        .from('performance_comments')
        .select(`
          *,
          profiles:user_id (
            username,
            avatar_url
          )
        `)
        .eq('performance_id', performanceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Format data to include joined fields at the top level
      return (data || []).map(item => ({
        ...item,
        username: item.profiles?.username,
        avatar_url: item.profiles?.avatar_url,
        profiles: undefined // Remove nested profiles object
      }));
    } catch (error) {
      console.error('Error getting comments:', error);
      throw error;
    }
  }

  async deleteComment(userId: string, commentId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('performance_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }

  async sendDirectMessage(senderId: string, recipientId: string, message: string): Promise<DirectMessage> {
    try {
      const { data, error } = await supabase
        .from('direct_messages')
        .insert([{
          sender_id: senderId,
          recipient_id: recipientId,
          message
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async getDirectMessages(userId: string, otherUserId: string): Promise<DirectMessage[]> {
    try {
      const { data, error } = await supabase
        .from('direct_messages')
        .select(`
          *,
          sender:sender_id (
            username,
            avatar_url
          )
        `)
        .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
        .or(`sender_id.eq.${otherUserId},recipient_id.eq.${otherUserId}`)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Format data to include joined fields at the top level
      return (data || []).map(item => ({
        ...item,
        sender_username: item.sender?.username,
        sender_avatar_url: item.sender?.avatar_url,
        sender: undefined // Remove nested sender object
      }));
    } catch (error) {
      console.error('Error getting messages:', error);
      throw error;
    }
  }

  async markMessagesAsRead(userId: string, otherUserId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('direct_messages')
        .update({ read: true })
        .eq('recipient_id', userId)
        .eq('sender_id', otherUserId)
        .eq('read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  }

  async getUnreadMessageCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('direct_messages')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('read', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error getting unread message count:', error);
      throw error;
    }
  }
}